import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { getInviteInfo, joinViaInvite } from "../api/invite";
import { login as apiLogin } from "../api/auth";
import { setAuthToken } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { isTokenExpired } from "../auth/jwt";

const GAME_DESCRIPTIONS = {
  pl: {
    title: "PL Pick 'Em",
    blurb:
      "Each matchweek, pick which teams you think will win. Points are awarded for correct picks — consistency and strategy win the season.",
  },
  mlb: {
    title: "MLB HR Derby",
    blurb:
      "Draft a roster of MLB players and score points based on their home run totals throughout the season. Manage your roster with weekly swaps.",
  },
  wc: {
    title: "World Cup Draft",
    blurb:
      "Draft national teams and earn points as they advance through the tournament. Your teams' results determine your final standing.",
  },
};

export default function JoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthed, loginWithSession, logout } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  // new-user flow
  const [email, setEmail] = useState("");

  // existing-user inline sign-in
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState("");

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");

  useEffect(() => {
    // An expired JWT still sitting in localStorage makes the backend treat this
    // request as anonymous (it silently downgrades instead of raising 401), which
    // sends a logged-in user down the "enter email" guest flow. Catch it here by
    // checking expiry client-side and sending them to re-login first.
    const storedJwt = localStorage.getItem("deepend.jwt");
    if (storedJwt && isTokenExpired(storedJwt)) {
      localStorage.removeItem("deepend.jwt");
      localStorage.removeItem("deepend.user");
      logout();
      navigate(`/login?redirect=${encodeURIComponent(`/join/${token}`)}`, { replace: true });
      return;
    }

    async function load() {
      try {
        const data = await getInviteInfo(token);
        setInfo(data);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "This invite link is invalid or has expired.";
        setLoadError(typeof msg === "string" ? msg : "This invite link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function doJoin(authOverride) {
    setJoinError("");
    setJoining(true);
    try {
      const result = await joinViaInvite(token, authOverride ?? (isAuthed ? {} : { email }));
      if (result.status === "created") {
        loginWithSession({ token: result.access_token, user: result.user });
      }
      navigate(result.redirect_path, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setJoinError(
        typeof detail === "string"
          ? detail
          : err?.message || "Failed to join pool. Please try again."
      );
    } finally {
      setJoining(false);
    }
  }

  async function handleSignInAndJoin(e) {
    e.preventDefault();
    setSignInError("");
    setSigningIn(true);
    try {
      const { token: jwt, user } = await apiLogin({ email: signInEmail, password: signInPassword });
      setAuthToken(jwt);           // update axios immediately so the join request has the Bearer token
      loginWithSession({ token: jwt, user });
      await doJoin({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSignInError(
        typeof detail === "string"
          ? detail
          : err?.message || "Sign in failed. Check your email and password."
      );
    } finally {
      setSigningIn(false);
    }
  }

  const desc = info ? GAME_DESCRIPTIONS[info.game] : null;
  const emailTrimmed = email.trim();
  const newUserEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);

  return (
    <>
      <style>{`
        .joinBackground {
          background: linear-gradient(120deg,#0a3d62,#1e81b0,#3caea3,#1e81b0,#0a3d62);
          background-size: 400% 400%;
          animation: joinShimmer 8s ease infinite;
        }
        @keyframes joinShimmer {
          0% { background-position: 0% 50%; filter: brightness(100%); }
          50% { background-position: 100% 50%; filter: brightness(120%); }
          100% { background-position: 0% 50%; filter: brightness(100%); }
        }
      `}</style>

      <Box minH="100vh" className="joinBackground" color="white">
        <Box bg="rgba(0,0,40,0.45)" minH="100vh" px={4} py={20}>
          <Container maxW="container.sm" centerContent>
            <VStack spacing={6} align="stretch" w="100%">

              <Box bg="#00007f" px={8} py={6} borderRadius="md" boxShadow="lg" textAlign="center">
                <Heading size={{ base: "lg", md: "xl" }} fontFamily="'Orbitron', sans-serif" letterSpacing="2px">
                  THE DEEP END
                </Heading>
                <Text mt={2} fontSize="md" color="whiteAlpha.800">
                  You've been invited to join a pool
                </Text>
              </Box>

              {loading ? (
                <Box textAlign="center" py={10}>
                  <Spinner size="xl" />
                </Box>
              ) : loadError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {loadError}
                </Alert>
              ) : (
                <Box
                  bg="rgba(255,255,255,0.15)"
                  px={6}
                  py={8}
                  borderRadius="md"
                  boxShadow="xl"
                  backdropFilter="blur(6px)"
                >
                  <VStack spacing={5} align="stretch">
                    <VStack spacing={1} align="start">
                      <Text fontSize="xs" textTransform="uppercase" letterSpacing="1px" color="orange.200">
                        {desc?.title}
                      </Text>
                      <Heading size="lg">{info.pool_name}</Heading>
                      <Text fontSize="sm" color="whiteAlpha.800" pt={1}>
                        {desc?.blurb}
                      </Text>
                    </VStack>

                    {joinError && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {joinError}
                      </Alert>
                    )}

                    {isAuthed ? (
                      /* Already logged in — one click */
                      <Button
                        bg="#00007f"
                        color="white"
                        size="lg"
                        w="100%"
                        isLoading={joining}
                        loadingText="Joining..."
                        onClick={() => doJoin()}
                        _hover={{ bg: "#000099" }}
                      >
                        Join Pool
                      </Button>
                    ) : showSignIn ? (
                      /* Inline sign-in form */
                      <Box as="form" onSubmit={handleSignInAndJoin}>
                        <VStack spacing={4} align="stretch">
                          {signInError && (
                            <Alert status="error" borderRadius="md">
                              <AlertIcon />
                              {signInError}
                            </Alert>
                          )}
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Email</FormLabel>
                            <Input
                              type="email"
                              variant="filled"
                              bg="white"
                              color="black"
                              value={signInEmail}
                              onChange={(e) => setSignInEmail(e.target.value)}
                              autoComplete="email"
                              autoFocus
                            />
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Password</FormLabel>
                            <Input
                              type="password"
                              variant="filled"
                              bg="white"
                              color="black"
                              value={signInPassword}
                              onChange={(e) => setSignInPassword(e.target.value)}
                              autoComplete="current-password"
                            />
                          </FormControl>
                          <Button
                            type="submit"
                            bg="#00007f"
                            color="white"
                            size="lg"
                            w="100%"
                            isLoading={signingIn || joining}
                            loadingText={signingIn ? "Signing in..." : "Joining..."}
                            _hover={{ bg: "#000099" }}
                          >
                            Sign In & Join Pool
                          </Button>
                          <Text
                            fontSize="sm"
                            color="orange.200"
                            fontWeight="600"
                            cursor="pointer"
                            textAlign="center"
                            onClick={() => { setShowSignIn(false); setSignInError(""); }}
                          >
                            ← New here? Create an account instead
                          </Text>
                        </VStack>
                      </Box>
                    ) : (
                      /* New-user email entry */
                      <VStack spacing={4} align="stretch">
                        <FormControl>
                          <FormLabel fontSize="sm">Your Email Address</FormLabel>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            variant="filled"
                            bg="white"
                            color="black"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                          />
                          <Text fontSize="xs" color="whiteAlpha.600" mt={1}>
                            A new account will be created. We'll email you a link to set your password.
                          </Text>
                        </FormControl>

                        <Button
                          bg="#00007f"
                          color="white"
                          size="lg"
                          w="100%"
                          isLoading={joining}
                          loadingText="Joining..."
                          isDisabled={!newUserEmailValid}
                          onClick={() => doJoin()}
                          _hover={{ bg: "#000099" }}
                        >
                          Join Pool
                        </Button>

                        <Divider borderColor="whiteAlpha.400" />

                        <Text fontSize="sm" textAlign="center" color="whiteAlpha.800">
                          Already have an account?{" "}
                          <Text
                            as="span"
                            color="orange.200"
                            fontWeight="600"
                            cursor="pointer"
                            onClick={() => { setShowSignIn(true); setJoinError(""); }}
                          >
                            Sign in here
                          </Text>
                        </Text>
                      </VStack>
                    )}
                  </VStack>
                </Box>
              )}

            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}
